import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  IdentityMatchResult,
  IdentityMatchUtils,
  IdentityVerificationStatus,
  UserForAdminDto,
} from '@workspace/data-types';
import { DialogLayoutAuraComponent } from '@workspace/ng-aura';

export interface StripeVerifiedIdentityData {
  firstName: string;
  lastName: string;
  type?: string;
  issuingCountry?: string;
}

export interface IdentityReviewDialogData {
  user: UserForAdminDto;
  stripeIdentity?: StripeVerifiedIdentityData | null;
}

export type ReviewDecisionType = 'VALIDATE_WITH_ID' | 'VALIDATE_PROFILE' | 'REJECT';

export interface IdentityReviewDialogResult {
  status: IdentityVerificationStatus;
  applyIdentityData: boolean;
}

@Component({
  selector: 'core-identity-review-dialog',
  templateUrl: './identity-review-dialog.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TitleCasePipe, MatDialogModule, MatButtonModule, MatIconModule, DialogLayoutAuraComponent],
})
export class IdentityReviewDialogCoreComponent {
  readonly dialogRef = inject(MatDialogRef<IdentityReviewDialogCoreComponent>);
  readonly data: IdentityReviewDialogData = inject(MAT_DIALOG_DATA);

  readonly identityVerificationStatus = IdentityVerificationStatus;

  readonly hasStripeIdentity = computed(() => {
    const s = this.data.stripeIdentity;
    return Boolean(s && (s.firstName || s.lastName));
  });

  readonly matchResult = computed<IdentityMatchResult | null>(() => {
    const stripe = this.data.stripeIdentity;
    const user = this.data.user;
    if (!stripe || (!stripe.firstName && !stripe.lastName)) {
      return null;
    }
    return IdentityMatchUtils.calculateMatch(
      stripe.firstName || '',
      stripe.lastName || '',
      user.identity?.firstName || '',
      user.identity?.lastName || '',
    );
  });

  readonly matchBadgeClass = computed(() => {
    const res = this.matchResult();
    if (!res) return '';
    if (res.score >= 85) {
      return 'bg-jade-3 text-jade-11 border-jade-6';
    }
    if (res.score >= 70) {
      return 'bg-amber-3 text-amber-11 border-amber-6';
    }
    return 'bg-ruby-3 text-ruby-11 border-ruby-6';
  });

  readonly selectedDecision = signal<ReviewDecisionType>(
    this.hasStripeIdentity() ? 'VALIDATE_WITH_ID' : 'VALIDATE_PROFILE',
  );

  selectDecision(decision: ReviewDecisionType): void {
    this.selectedDecision.set(decision);
  }

  readonly previewData = computed(() => {
    const decision = this.selectedDecision();
    const user = this.data.user;
    const stripe = this.data.stripeIdentity;

    switch (decision) {
      case 'VALIDATE_WITH_ID':
        return {
          firstName: stripe?.firstName || user.identity?.firstName || '',
          lastName: stripe?.lastName || user.identity?.lastName || '',
          status: IdentityVerificationStatus.VERIFIED,
          statusLabel: 'Vérifié',
          statusClass: 'bg-jade-3 text-jade-12 border-jade-6',
          statusDotClass: 'bg-jade-9',
          impactNote:
            "Le prénom et le nom du profil seront écrasés par ceux de la pièce d'identité Stripe. Les comptes Keycloak et l'index de recherche seront synchronisés.",
          isDestructive: false,
        };
      case 'VALIDATE_PROFILE':
        return {
          firstName: user.identity?.firstName || '',
          lastName: user.identity?.lastName || '',
          status: IdentityVerificationStatus.VERIFIED,
          statusLabel: 'Vérifié',
          statusClass: 'bg-jade-3 text-jade-12 border-jade-6',
          statusDotClass: 'bg-jade-9',
          impactNote:
            "Le nom actuel du profil utilisateur est conservé tel quel. L'identité est validée manuellement par l'administrateur.",
          isDestructive: false,
        };
      case 'REJECT':
        return {
          firstName: user.identity?.firstName || '',
          lastName: user.identity?.lastName || '',
          status: user.identity?.verificationStatus ?? IdentityVerificationStatus.UNVERIFIED,
          statusLabel: 'Non vérifié',
          statusClass: 'bg-neutral-3 text-neutral-12 border-neutral-6',
          statusDotClass: 'bg-neutral-9',
          impactNote:
            "La vérification est refusée. Le profil reste non vérifié. L'utilisateur devra soumettre une nouvelle pièce d'identité conforme pour débloquer son compte.",
          isDestructive: true,
        };
    }
  });

  onConfirm(): void {
    const decision = this.selectedDecision();
    let result: IdentityReviewDialogResult;

    switch (decision) {
      case 'VALIDATE_WITH_ID':
        result = {
          status: IdentityVerificationStatus.VERIFIED,
          applyIdentityData: true,
        };
        break;
      case 'VALIDATE_PROFILE':
        result = {
          status: IdentityVerificationStatus.VERIFIED,
          applyIdentityData: false,
        };
        break;
      case 'REJECT':
        result = {
          status: IdentityVerificationStatus.FAILED,
          applyIdentityData: false,
        };
        break;
    }

    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
